import { useState } from 'react';
import { api } from '../lib/api';
import { Card, Empty, ErrorNote, Hash, Loading, Pill, useResource, useToast } from './ui';
import { dateTime, retention } from '../lib/format';

/**
 * Record of processing activities.
 *
 * One record per purpose, read from the purpose register, the published
 * notices and the consent ledger. Nothing on this screen is entered by hand,
 * so the record cannot disagree with the notice a data principal was shown.
 */

interface Activity {
  ref: string;
  key: string;
  name: string;
  description: string;
  status: string;
  version: number;
  versionSince: string;
  dataCategories: string[];
  basis: 'consent' | 'legitimate_use';
  basisSubtype: string | null;
  retentionDays: number | null;
  requiredForService: boolean;
  processors: string[];
  collectionPoints: Array<{ name: string; channel: string; status: string }>;
  notices: Array<{ key: string; title: string; version: number; languages: string[]; publishedAt: string | null }>;
  principals: { live: number; declined: number; withdrawn: number; expired: number };
}

interface Ropa {
  tenantName: string;
  generatedAt: string;
  grievanceOfficer: { name: string; email: string; phone: string; address: string } | null;
  activities: Activity[];
  contentHash: string;
  signature: { keyId: string };
}

export default function RegisterCard() {
  const toast = useToast();
  const [includeRetired, setIncludeRetired] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const ropa = useResource<Ropa>(
    () => api.get(`/v1/reports/ropa?includeRetired=${includeRetired}`), [includeRetired],
  );

  async function download() {
    setBusy(true);
    try {
      await api.download(
        `/v1/reports/ropa.pdf?includeRetired=${includeRetired}`,
        `record-of-processing-${new Date().toISOString().slice(0, 10)}.pdf`,
      );
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  const rows = ropa.data?.activities ?? [];
  const live = rows.reduce((n, a) => n + a.principals.live, 0);
  const processors = new Set(rows.flatMap((a) => a.processors));
  const uncovered = rows.filter((a) => a.status === 'active' && a.notices.length === 0);

  return (
    <>
      <ErrorNote error={ropa.error} />
      {ropa.loading && !ropa.data && <Loading what="Building register" />}
      {ropa.data && (
        <>
          <Card title="Processing register">
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10 }}>
              <label className="check">
                <input type="checkbox" checked={includeRetired} onChange={(e) => setIncludeRetired(e.target.checked)} />
                <span>Include retired</span>
              </label>
              <span className="small muted">{rows.length} purposes · {live} live consents · {processors.size} processors{uncovered.length ? ` · ${uncovered.length} without a published notice` : ''}</span>
              <button className="small" onClick={download} disabled={busy || !ropa.data} style={{ marginInlineStart: 'auto' }}>{busy ? 'Generating…' : 'Download signed PDF'}</button>
            </div>
            {rows.length === 0 ? <Empty>No processing activities.</Empty> : (
              <div className="table-scroll">
                <table className="data">
                  <thead>
                    <tr>
                      <th>Ref</th>
                      <th>Purpose</th>
                      <th>Categories of personal data</th>
                      <th>Lawful basis</th>
                      <th>Retention</th>
                      <th>Processors</th>
                      <th>Notice</th>
                      <th className="num">People with live consent</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((a) => (
                      <RowGroup key={a.key} a={a} open={open === a.key} onToggle={() => setOpen(open === a.key ? null : a.key)} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <Card title="Data Fiduciary">
            <dl className="kv">
              <dt>Fiduciary</dt><dd>{ropa.data.tenantName}</dd>
              {ropa.data.grievanceOfficer && (
                <>
                  <dt>Grievance Officer</dt><dd>{ropa.data.grievanceOfficer.name}</dd>
                  <dt>Contact</dt><dd>{ropa.data.grievanceOfficer.email} · {ropa.data.grievanceOfficer.phone}</dd>
                  <dt>Address</dt><dd>{ropa.data.grievanceOfficer.address}</dd>
                </>
              )}
              <dt>Generated</dt><dd>{dateTime(ropa.data.generatedAt)}</dd>
              <dt>Content hash</dt><dd><Hash value={ropa.data.contentHash} head={16} /></dd>
              <dt>Signed with</dt><dd className="mono">{ropa.data.signature.keyId}</dd>
            </dl>
          </Card>
        </>
      )}
    </>
  );
}

function RowGroup({ a, open, onToggle }: { a: Activity; open: boolean; onToggle: () => void }) {
  return (
    <>
      <tr onClick={onToggle} style={{ cursor: 'pointer' }}>
        <td className="small muted">{a.ref}</td>
        <td>
          <strong>{a.name}</strong>
          <div className="small muted">v{a.version}{a.status !== 'active' && <> · <Pill>{a.status}</Pill></>}</div>
        </td>
        <td className="small">{a.dataCategories.join(', ')}</td>
        <td className="small">
          {a.basis === 'consent' ? 'Consent (S.6)' : 'Legitimate use (S.7)'}
          {a.requiredForService && <div className="muted">Necessary for service</div>}
        </td>
        <td className="small">{retention(a.retentionDays)}</td>
        <td className="small">{a.processors.join(', ') || '—'}</td>
        <td className="small">
          {a.notices.length
            ? a.notices.map((n) => `${n.title} v${n.version}`).join(', ')
            : <Pill tone="bad">None</Pill>}
        </td>
        <td className="num">{a.principals.live}</td>
        <td className="small muted">{open ? '▾' : '▸'}</td>
      </tr>
      {open && (
        <tr>
          <td colSpan={9} style={{ background: 'var(--paper-2)' }}>
            <dl className="kv">
              <dt>Description</dt><dd>{a.description}</dd>
              <dt>Lawful basis</dt>
              <dd>{a.basis === 'consent' ? 'Consent (S.6)' : a.basisSubtype ?? 'Legitimate use (S.7)'}</dd>
              <dt>Version</dt><dd>v{a.version} since {a.versionSince.slice(0, 10)}</dd>
              <dt>Collection points</dt>
              <dd>{a.collectionPoints.map((cp) => `${cp.name} (${cp.channel})`).join('; ') || 'None'}</dd>
              <dt>Notice coverage</dt>
              <dd>{a.notices.map((n) => `${n.title} v${n.version} [${n.languages.join(', ')}]`).join('; ') || 'No published notice'}</dd>
              <dt>Data Principals</dt>
              <dd>
                {a.principals.live} active · {a.principals.declined} declined ·{' '}
                {a.principals.withdrawn} withdrawn · {a.principals.expired} expired
              </dd>
            </dl>
          </td>
        </tr>
      )}
    </>
  );
}
